import React, { useEffect, useState } from 'react';
import RegionSelector from '../RegionSelector/RegionSelector';
import Link from '../Link/Link';
import useRegionSelector from '../../../hooks/useRegionSelector';

import './index.scss';

const LinkItem = ({ link }) => {
  return (
    <li className="nav-item">
      <Link to={link}>{link.label}</Link>
    </li>
  );
};

const DropdownItem = ({ link, children }) => {
  const { region } = useRegionSelector();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const mouseEnter = () => setDropdownOpen(true);
  const mouseLeave = () => setDropdownOpen(false);

  const isCurrentUrl =
    typeof window !== 'undefined' &&
    children.some((child) => child.link?.slug === window.location.pathname.replaceAll('/', ''));

  return (
    <li className="dropdown nav-item" onMouseEnter={mouseEnter} onMouseLeave={mouseLeave}>
      <span
        to={'#'}
        className={`${isCurrentUrl ? 'active-link' : ''}`}
        type="button"
        aria-label="Expand"
        aria-expanded="false"
        data-bs-toggle="dropdown"
      >
        {link.label}
      </span>

      <ul className={`dropdown-menu ${dropdownOpen ? 'open' : null}`}>
        {children
          ?.filter((link) => (link.showOnlyOn.length === 0 ? true : link.showOnlyOn.some((r) => r.label === region)))
          .sort((a, b) => a.position - b.position)
          .map((link) => (
            <li className="dropdown-item" key={link.id}>
              <Link className="dropdown-link" to={link}>
                {link?.label}
              </Link>
            </li>
          ))}
      </ul>
    </li>
  );
};

export default function Nav({ logo, information, navData, setSearchEngineVisible }) {
  const navLinks = navData.nodes;

  const [expanded, setExpanded] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const handleScroll = () => {
    if (typeof window !== 'undefined') {
      const position = window.pageYOffset;
      setScrollPosition(position);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const handleNavClick = () => setExpanded(!expanded);

  const groupedLinks = navLinks.reduce(
    (result, item) => {
      if (item.alignOnRight) {
        result.right.push(item);
      } else {
        result.left.push(item);
      }
      return result;
    },
    { left: [], right: [] }
  );

  return (
    <nav className={`navbar navbar-expand-lg ${expanded ? 'expanded' : ''} ${scrollPosition > 75 ? 'sticky-nav' : ''}`}>
      <Link className="navbar-brand" to={'/'}>
        <img src={logo.url} alt="Positive Money Logo" />
      </Link>

      <button
        type="button"
        data-target="#navNav"
        aria-expanded="false"
        aria-controls="navNav"
        data-toggle="collapse"
        className="navbar-toggler"
        aria-label="Toggle navigation"
        onClick={() => handleNavClick()}
      >
        <span className={`${expanded ? 'open-toggle ' : ''} navbar-toggler-icon`} />
      </button>

      <div className={`${expanded ? 'show' : ''} collapse navbar-collapse`}>
        <ul className={`navbar-nav`}>
          <div className="main-links">
            {groupedLinks?.left?.map((link) =>
              link.treeChildren.length === 0 ? (
                <LinkItem key={link.id} link={link} label={link?.label} />
              ) : (
                <DropdownItem key={link.id} link={link} children={link?.treeChildren} />
              )
            )}
          </div>

          <div className="nav-actions">
            {groupedLinks?.right?.map((link) => (
              <LinkItem key={link.id} link={link} />
            ))}

            <div className="actions">
              <button onClick={() => setSearchEngineVisible(true)} style={{ cursor: 'pointer' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
                  <path
                    d="M29.7082 26.8334H28.194L27.6573 26.3159C28.8552 24.9243 29.7306 23.2852 30.221 21.5158C30.7114 19.7464 30.8046 17.8905 30.494 16.0809C29.5932 10.7526 25.1465 6.49757 19.7798 5.84591C17.8931 5.60722 15.9768 5.8033 14.1775 6.41916C12.3782 7.03502 10.7436 8.05433 9.39884 9.39908C8.05408 10.7438 7.03478 12.3784 6.41892 14.1777C5.80306 15.977 5.60697 17.8933 5.84566 19.7801C6.49733 25.1467 10.7523 29.5934 16.0807 30.4942C17.8903 30.8048 19.7462 30.7116 21.5156 30.2212C23.285 29.7308 24.9241 28.8554 26.3157 27.6576L26.8332 28.1942V29.7084L34.979 37.8542C35.7648 38.6401 37.049 38.6401 37.8348 37.8542C38.6207 37.0684 38.6207 35.7842 37.8348 34.9984L29.7082 26.8334ZM18.2082 26.8334C13.4357 26.8334 9.58316 22.9809 9.58316 18.2084C9.58316 13.4359 13.4357 9.58341 18.2082 9.58341C22.9807 9.58341 26.8332 13.4359 26.8332 18.2084C26.8332 22.9809 22.9807 26.8334 18.2082 26.8334Z"
                    fill="#011C25"
                  />
                </svg>
              </button>

              <RegionSelector logo={logo} information={information} onMobileClick={handleNavClick} />
            </div>
          </div>
        </ul>
      </div>
    </nav>
  );
}
